import { call, put, select } from 'redux-saga/effects';

import { StatusCodes } from 'config/global-enums';
import { createSelector } from '@reduxjs/toolkit';
import { message } from 'antd';
import { request } from 'utils/request';

export function* handleErrorMessage(errMsg, actions, actionName) {
  if (errMsg) {
    message.error(errMsg);
  }
  yield put(actions[actionName](errMsg));
}

export const sliceLoadStart = state => {
  state.error = null;
  state.loading = true;
};

export const sliceLoadFail = (state, action) => {
  state.error = action.payload;
  state.loading = false;
};

export const sliceClear = (state, action) => {
  state.error = null;
  state.loading = false;
  state.data = null;
};

export function* sagaRequestTemplate(
  requestURL,
  payload,
  actions,
  successActionName,
  errorActionName,
  errMsg,
  reqArgs = [],
) {
  try {
    const response = yield call(request, requestURL, payload, ...reqArgs);
    if (response.statusCode === StatusCodes.SUCCESS_CODE) {
      yield put(actions[successActionName](response?.rows));
    } else {
      yield call(
        handleErrorMessage,
        response?.message,
        actions,
        errorActionName,
      );
    }
  } catch (err) {
    yield call(handleErrorMessage, errMsg, actions, errorActionName);
  }
}

export function* handleSpecialErrorMessage(
  errMsg,
  actions,
  actionName,
  actionFor,
) {
  if (errMsg) {
    message.error(errMsg);
  }
  yield put(
    actions[actionName]({ error: errMsg, actionFor, responseData: null }),
  );
}

export const sliceSpecialLoadHandle = (
  state,
  actionFor,
  loading,
  data,
  page_number,
  page_size,
  total,
  searchValue,
  responseData,
) => {
  state[`${actionFor}Loading`] = loading;
  state[`${actionFor}Error`] = null;
  state[`${actionFor}SearchValue`] = searchValue || null;
  if (data) state[`${actionFor}Data`] = data;
  if (page_number) state[`${actionFor}PageNumber`] = page_number;
  if (page_size) state[`${actionFor}PageSize`] = page_size;
  if (total) state[`${actionFor}Total`] = total;
  if (responseData) state[`${actionFor}ResponseData`] = responseData;
};

export const sliceSpecialLoadFail = (state, actionFor, error) => {
  state[`${actionFor}Error`] = error;
  state[`${actionFor}Loading`] = false;
  state[`${actionFor}ResponseData`] = null;
};

export function* sagaSpecialRequestTemplate(
  requestURL,
  payload,
  actions,
  successActionName,
  errorActionName,
  errMsg,
  successMsg = 'Changes made successfully!',
  reqArgs = [],
  callbackFn = { successCb: null, failCb: null },
) {
  const { actionFor, ...restPayload } = payload;

  try {
    const response = yield call(request, requestURL, restPayload, ...reqArgs);
    if (response.statusCode === StatusCodes.SUCCESS_CODE) {
      if (callbackFn?.successCb) {
        callbackFn.successCb(response?.rows);
        return;
      }
      yield put(
        actions[successActionName]({
          data: response?.rows,
          actionFor,
          responseData: response,
        }),
      );
      successMsg && message.success(successMsg);
    } else {
      if (callbackFn?.failCb) {
        callbackFn.failCb(response);
        return;
      }
      yield call(
        handleSpecialErrorMessage,
        response?.message,
        actions,
        errorActionName,
        actionFor,
      );
    }
  } catch (err) {
    yield call(
      handleSpecialErrorMessage,
      errMsg,
      actions,
      errorActionName,
      actionFor,
    );
  }
}

export const specialStateSelector = (forSlice, statusFor) =>
  createSelector([forSlice], state => ({
    loading: state?.[`${statusFor}Loading`],
    error: state?.[`${statusFor}Error`],
    data: state?.[`${statusFor}Data`],
    page_number: state?.[`${statusFor}PageNumber`] || 1,
    page_size: state?.[`${statusFor}PageSize`] || 15,
    total: state?.[`${statusFor}Total`] || 0,
    searchValue: state?.[`${statusFor}SearchValue`] || null,
    responseData: state?.[`${statusFor}ResponseData`] || null,
  }));

export const sagaSpecialClearState = (state, actionFor) => {
  state[`${actionFor}Data`] = null;
  state[`${actionFor}Error`] = null;
  state[`${actionFor}Loading`] = false;
  state[`${actionFor}ResponseData`] = null;
  state[`${actionFor}Total`] = 0;
};

export function* sagaPaginationRequestTemplate(
  requestURL,
  payload,
  actions,
  successActionName,
  errorActionName,
  errMsg,
  selectSlice,
  reqArgs = [],
) {
  const {
    page_number: oldPageNumber,
    page_size: oldPageSize,
    data: oldData,
    // total: oldTotal,
  } = yield select(specialStateSelector(selectSlice, payload?.actionFor));

  const {
    actionFor,
    page_number = oldPageNumber,
    page_size = oldPageSize,
    updatePageSize = false,
    isTable = false,
    searchValue = null,
    ...restPayload
  } = payload;

  const newPageNumber =
    page_size > oldPageSize
      ? page_number + Math.ceil(page_size / oldPageSize)
      : page_number + 1;

  try {
    const response = yield call(
      request,
      requestURL,
      {
        page_number,
        page_size,
        ...restPayload,
      },
      ...reqArgs,
    );
    if (response.statusCode === StatusCodes.SUCCESS_CODE) {
      const newData = response?.rows;

      const finalData =
        isTable || !page_number || page_number === 1
          ? newData || []
          : oldData.concat(newData || []);
      const total = response?.total;

      yield put(
        actions[successActionName]({
          data: finalData,
          actionFor,
          page_number: newData?.length ? newPageNumber : page_number,
          page_size: updatePageSize ? page_size : oldPageSize,
          total,
          searchValue,
          responseData: response,
        }),
      );
    } else {
      const errorMessage =
        response?.responseData?.error ||
        response?.message ||
        'Something went wrong';

      yield call(
        handleSpecialErrorMessage,
        errorMessage,
        actions,
        errorActionName,
        actionFor,
      );
    }
  } catch (err) {
    const errorMessage =
      err?.responseData?.error || err?.message || 'Something went wrong';

    yield call(
      handleSpecialErrorMessage,
      errorMessage,
      actions,
      errorActionName,
      actionFor,
    );
  }
}
